import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { API_URL } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Use sessionStorage for multi-tab testing (each tab = separate user)
  // Switch to localStorage if you want persistence across browser restarts
  const storage = sessionStorage;

  useEffect(() => {
    if (window.__COOKUP_CHAT_PLAYER__) { setLoading(false); return; }
    // Check for stored token on mount
    try {
      let storedToken = storage.getItem('token');
      let storedUser = storage.getItem('user');

      if (!storedToken || !storedUser) {
        const handoffRaw = localStorage.getItem('cookup_auth_handoff');
        if (handoffRaw) {
          try {
            const handoff = JSON.parse(handoffRaw);
            if (handoff?.token && handoff?.user && Number(handoff?.expiresAt) > Date.now()) {
              storedToken = handoff.token;
              storedUser = handoff.user;
              storage.setItem('token', storedToken);
              storage.setItem('user', storedUser);
            }
          } finally {
            localStorage.removeItem('cookup_auth_handoff');
          }
        }
      }

      // console.log('Checking storage:', { storedToken: !!storedToken, storedUser: !!storedUser });

      if (storedToken && storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
        // console.log('Restored session:', parsedUser.username);
      }
    } catch (error) {
      console.error('Error restoring session:', error);
      // Clear corrupted data
      storage.removeItem('token');
      storage.removeItem('user');
    }

    setLoading(false);
  }, [storage]);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const body = await response.json();
        const error = new Error(body.error || 'Login failed');
        error.code = body.code;
        error.verificationRequired = Boolean(body.verificationRequired);
        throw error;
      }

      const data = await response.json();

      setToken(data.token);
      setUser(data.user);

      storage.setItem('token', data.token);
      storage.setItem('user', JSON.stringify(data.user));

      // console.log('Login successful, stored in sessionStorage:', data.user.username);

      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  /** Store a session issued by an external sign-in (Sign in with ChatGPT) like a password login. */
  const completeExternalLogin = useCallback((data) => {
    setToken(data.token);
    setUser(data.user);
    storage.setItem('token', data.token);
    storage.setItem('user', JSON.stringify(data.user));
  }, [storage]);

  const register = async (username, email, password, acceptTerms = false) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password, acceptTerms })
      });

      if (!response.ok) {
        const error = await response.json();
        // Show detailed validation errors if available
        if (error.details) {
          const errorMessages = error.details.map(d => d.msg).join(', ');
          throw new Error(`${error.error}: ${errorMessages}`);
        }
        throw new Error(error.error || 'Registration failed');
      }

      const data = await response.json();

      if (data.token && data.user) {
        setToken(data.token);
        setUser(data.user);
        storage.setItem('token', data.token);
        storage.setItem('user', JSON.stringify(data.user));
      }

      return data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const requestPasswordReset = async (email) => {
    const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Unable to request password reset');
    return data;
  };

  const resendVerificationEmail = async (email) => {
    const response = await fetch(`${API_URL}/api/auth/verify-email/resend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Unable to resend verification email');
    return data;
  };

  const resetPassword = async (resetToken, password) => {
    const response = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: resetToken, password })
    });
    const data = await response.json();
    if (!response.ok) {
      const details = Array.isArray(data.details) ? data.details.map((item) => item.msg).join(', ') : '';
      throw new Error(details || data.error || 'Unable to reset password');
    }
    return data;
  };

  const requestAccountDeletion = async () => {
    const response = await fetch(`${API_URL}/api/auth/account/deletion-request`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Unable to request account deletion');
    return data;
  };

  const accountDeletionRequest = async (method = 'GET') => {
    const response = await fetch(`${API_URL}/api/auth/account/deletion-request${method === 'CANCEL' ? '/cancel' : ''}`, {
      method: method === 'CANCEL' ? 'POST' : 'GET',
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Unable to load the account deletion request');
    return data;
  };

  const logout = async () => {
    const activeToken = token;
    setToken(null);
    setUser(null);
    storage.removeItem('token');
    storage.removeItem('user');
    if (activeToken) {
      try {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${activeToken}` }
        });
      } catch {
        // Local logout must still succeed when the network is unavailable.
      }
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    completeExternalLogin,
    register,
    resendVerificationEmail,
    requestPasswordReset,
    resetPassword,
    requestAccountDeletion,
    accountDeletionRequest,
    logout,
    setUser,
    isAuthenticated: !!token
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
